import { createContext, useContext, useState } from 'preact/compat';
import { useEffect, useCallback }              from 'preact/hooks';

import { useAppContext, useUserContext } from '../index';
import { createBehavioralFetchers }     from './behavioralContext.fetchers';
import { isEmptyObject }                from './behavioralContext.utils';
import {
    resolveProductPageCartState,
    shouldTriggerWarehouseFlow,
    buildSimpleCartUpdate,
} from './behavioralContext.cart';

// ─────────────────────────────────────────────
// Initial states
// ─────────────────────────────────────────────

const INITIAL_SEARCH_BAR = {
    isOpen:              false,
    query:               '',
    data:                [],
    recommendedProducts: null, // Legacy property
};

const INITIAL_SIDEBAR_CART = {
    isOpen:                   false,
    newProductData:           {},
    url:                      '',
    lastWarehouseAdded:       {},
    isSameWarehouseModalOpen: false,
    modalBypass:              false,
};

const INITIAL_ALERT = {
    isOpen:      false,
    productName: '',
};

const ALERT_CLEAR_DELAY_MS = 4000;

// ─────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────

const BehavioralContext = createContext(null);

// TODO: most of the states in here can be refactored into reducers
//  to reduce both the code complexity and amount.
export const BehavioralContextProvider = ({ children }) => {

    // ── State ─────────────────────────────────

    const [searchBarData,   setSearchBarData]   = useState(INITIAL_SEARCH_BAR);
    const [sidebarCartData, setSidebarCartData] = useState(INITIAL_SIDEBAR_CART);
    const [isMenuOpen,      setIsMenuOpen]      = useState(false);
    const [newItemAlertStatus,  setNewItemAlertStatus]  = useState(INITIAL_ALERT);
    const [newsLetterSubmitted, setNewsLetterSubmitted] = useState(false);

    const [isSelfServiceAttendanceModalOpen, setIsSelfServiceAttendanceModalOpen] = useState(false);
    const [selfServiceAttendanceModalData,   setSelfServiceAttendanceModalData]   = useState(null);

    // ── Context dependencies ──────────────────

    const { storeLocation, setFullPageLoader } = useAppContext();

    const [{}, {}, {
        cartData,
        acceptedTermUse,
        acceptedExamsUseTerms
    }] = useUserContext();
    const { qtyItems } = cartData;

    // ── Fetchers ──────────────────────────────

    const { productDataFetcher, selfServiceAttendanceModalDataFetcher } =
        createBehavioralFetchers({
            sidebarCartData,
            storeLocation,
            setFullPageLoader,
            acceptedTermUse,
            acceptedExamsUseTerms,
        });

    // ── Search bar ────────────────────────────

    const handleSearchBar = (target = undefined, state = undefined) => {
        if (!target || !Object.keys(searchBarData).includes(target)) return;

        setSearchBarData(prev => ({ ...prev, [target]: state }));
    };

    // ── Sidebar cart ──────────────────────────

    const handleCart = useCallback((
        target  = undefined,
        state   = undefined,
        dataSet = undefined,
    ) => {
        if (!target) return;

        const { lastWarehouseAdded } = sidebarCartData;

        if (shouldTriggerWarehouseFlow({ dataSet, lastWarehouseAdded, qtyItems })) {
            if (dataSet?.source === 'productPage') {
                const resolved = resolveProductPageCartState({
                    dataSet,
                    lastWarehouseAdded,
                    acceptedTermUse,
                    acceptedExamsUseTerms,
                });

                setSidebarCartData(prev => ({ ...prev, ...resolved }));
                return;
            }

            // Non-product-page: fetch the product data before opening the cart
            productDataFetcher(dataSet).then(res => {
                if (!res || isEmptyObject(res)) return;
                setSidebarCartData(prev => ({ ...prev, ...res }));
            });

            return;
        }

        setSidebarCartData(prev =>
            dataSet
                ? { ...prev, ...dataSet }
                : buildSimpleCartUpdate(prev, target, state)
        );
    }, [sidebarCartData, qtyItems, acceptedTermUse, acceptedExamsUseTerms]);

    // ── Menu ──────────────────────────────────

    const handleMenu = (state = undefined) => {
        setIsMenuOpen(typeof state === 'boolean' ? state : prev => !prev);
    };

    // ── New item alert ────────────────────────

    const handleNewItemAlertStatus = (data = undefined) => {
        if (!data) return;
        setNewItemAlertStatus({ ...data });
    };

    // Auto-clears the alert after a delay when it opens
    useEffect(() => {
        if (!newItemAlertStatus.isOpen) return;

        const timer = setTimeout(() => {
            setNewItemAlertStatus(INITIAL_ALERT);
        }, ALERT_CLEAR_DELAY_MS);

        return () => clearTimeout(timer);
    }, [newItemAlertStatus.isOpen]);

    // ── Self-service attendance modal ─────────

    const handleSelfServiceAttendanceModal = async (state = undefined) => {
        if (selfServiceAttendanceModalData === null) {
            const data = await selfServiceAttendanceModalDataFetcher();

            if (!data) return;

            setSelfServiceAttendanceModalData(data);
        }

        setIsSelfServiceAttendanceModalOpen(
            typeof state === 'boolean' ? state : prev => !prev
        );
    };

    // ─────────────────────────────────────────
    // Context value
    // ─────────────────────────────────────────

    const value = {
        searchBarData,
        sidebarCartData,
        isMenuOpen,
        newItemAlertStatus,
        newsLetterSubmitted,
        isSelfServiceAttendanceModalOpen,
        selfServiceAttendanceModalData,
        handleSearchBar,
        handleCart,
        handleMenu,
        handleNewItemAlertStatus,
        setNewsLetterSubmitted,
        handleSelfServiceAttendanceModal,
    };

    return (
        <BehavioralContext.Provider value={value}>
            {children}
        </BehavioralContext.Provider>
    );
};

const useBehavioralContext = () => useContext(BehavioralContext);
export default useBehavioralContext;
